/**
 * TransactionTable — transaction grid with running balance.
 *
 * SCOPE: HIL editing only. Used by ScanReviewPanel (must opt into editable=true)
 * to edit transactions inside the OCR scan review flow. NOT for displaying
 * published transactions to users — that path is the `transaction-viewing`
 * skill: agent calls render_transaction_analysis → TransactionAnalyzerPanel
 * → useTransactionDrilldown → TransactionAnalyzer (with filters, sankey,
 * PDF, edit hooks). Do not add this component to new agent renders or
 * dashboard surfaces.
 *
 * Props:
 *   transactions: Array<{ id, date, description, amount, page_number }>
 *     `desc` is accepted as an alias for `description` so the agent's
 *     compact query_transactions format renders correctly.
 *   columns: Array<string> — which columns to show
 *   title: string — optional title
 *   beginBalance: number — starting balance for running balance
 *   endBalance: number — scanned ending balance for comparison
 *   highlightIds: Array<string> — transaction IDs to highlight
 *   selectedId: string — externally controlled selected ID
 *   onInteraction: (event) => void — callback for clicks, edits, copy-to-chat
 *   editable: boolean — enable inline editing + add/delete UI (default false)
 */

import React, { useState, useCallback, useMemo } from 'react';
import styles from './TransactionTable.module.css';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ConfirmBeforeActionDialog from '../ui/dialog-confirm-before-action';

function TransactionTable({
  transactions: rawTransactions = [],
  columns,
  title,
  beginBalance,
  endBalance,
  highlightIds = [],
  selectedId: externalSelectedId,
  onInteraction,
  onChange,  // (newTransactions) => void — report merged state to parent
  editable = false,
}) {
  // Normalize the agent's compact `desc` field to `description` so a
  // single rendering path covers both data sources. Other field names
  // (date, amount) already match between the two sources.
  const transactions = useMemo(
    () => rawTransactions.map(t =>
      (t && t.description === undefined && t.desc !== undefined)
        ? { ...t, description: t.desc }
        : t
    ),
    [rawTransactions]
  );
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [internalSelectedId, setInternalSelectedId] = useState(null);
  const [corrections, setCorrections] = useState({});  // { txnId: { field: newValue } }
  const [undoStack, setUndoStack] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // { txnId, field }
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [addedRows, setAddedRows] = useState([]);
  const [deletedIds, setDeletedIds] = useState(new Set());

  const selectedId = externalSelectedId || internalSelectedId;
  const showPage = transactions.some(t => t.page_number);
  const showRunningBalance = beginBalance != null;
  const visibleColumns = columns || ['date', 'description', 'amount'];

  // Apply corrections to get effective values
  const getEffective = useCallback((tx, field) => {
    const corr = corrections[tx.id];
    if (corr && corr[field] !== undefined) return corr[field];
    return tx[field];
  }, [corrections]);

  // Merge transactions with corrections and additions, exclude deletions.
  // Inserted rows go after the row they were inserted from.
  const effectiveTransactions = useMemo(() => {
    const base = transactions.filter(t => !deletedIds.has(t.id));
    const result = [];
    const insertAfterMap = {};
    const appendRows = [];

    // Group added rows by their _insertAfter target
    for (const row of addedRows) {
      if (row._insertAfter) {
        if (!insertAfterMap[row._insertAfter]) insertAfterMap[row._insertAfter] = [];
        insertAfterMap[row._insertAfter].push(row);
      } else {
        appendRows.push(row);
      }
    }

    // Build result: base rows with inserted rows spliced in
    for (const tx of base) {
      result.push(tx);
      if (insertAfterMap[tx.id]) {
        result.push(...insertAfterMap[tx.id]);
      }
    }

    // Rows inserted after other added rows
    for (const row of appendRows) {
      result.push(row);
      if (insertAfterMap[row.id]) {
        result.push(...insertAfterMap[row.id]);
      }
    }

    return result;
  }, [transactions, addedRows, deletedIds]);

  // Sort
  const sorted = sortField
    ? [...effectiveTransactions].sort((a, b) => {
        const aVal = getEffective(a, sortField) || '';
        const bVal = getEffective(b, sortField) || '';
        if (sortField === 'amount') {
          return sortDir === 'asc'
            ? (parseFloat(aVal) - parseFloat(bVal))
            : (parseFloat(bVal) - parseFloat(aVal));
        }
        return sortDir === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      })
    : effectiveTransactions;

  // Running balance
  let runningBal = beginBalance || 0;
  const runningBalances = sorted.map(tx => {
    runningBal += (parseFloat(getEffective(tx, 'amount')) || 0);
    return Math.round(runningBal * 100) / 100;
  });
  const computedEndBalance = runningBalances.length > 0 ? runningBalances[runningBalances.length - 1] : beginBalance;
  const hasDiscrepancy = endBalance != null && Math.abs(computedEndBalance - endBalance) >= 0.01;
  const hasCorrections = Object.keys(corrections).length > 0 || addedRows.length > 0 || deletedIds.size > 0;

  // Report merged state to parent when onChange is provided
  const reportChange = useCallback((nextCorrections, nextAddedRows, nextDeletedIds) => {
    if (!onChange) return;
    // Build merged transactions: apply corrections, add new rows, remove deleted
    const base = transactions.filter(t => !nextDeletedIds.has(t.id));
    const merged = base.map(t => {
      const corr = nextCorrections[t.id];
      return corr ? { ...t, ...corr } : t;
    });
    // Add new rows with corrections applied
    const added = nextAddedRows.map(r => {
      const corr = nextCorrections[r.id];
      return corr ? { ...r, ...corr } : r;
    });
    onChange([...merged, ...added]);
  }, [onChange, transactions]);

  const handleHeaderClick = (field) => {
    if (field === sortField) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortField(null); setSortDir('asc'); }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleRowClick = (tx) => {
    setInternalSelectedId(tx.id);
    // Build effective data with corrections applied
    const effectiveData = { ...tx };
    const corr = corrections[tx.id];
    if (corr) Object.assign(effectiveData, corr);
    if (onInteraction) {
      onInteraction({ type: 'transaction_selected', transactionId: tx.id, data: effectiveData });
    }
  };

  // Copy transaction to chat
  const handleCopyToChat = (e, tx, rowNum) => {
    e.stopPropagation();
    const effectiveData = { ...tx };
    const corr = corrections[tx.id];
    if (corr) Object.assign(effectiveData, corr);
    if (onInteraction) {
      onInteraction({
        type: 'copy_to_chat',
        transactionId: tx.id,
        message: `Row ${rowNum} [${tx.id}]: ${getEffective(tx, 'description')} | ${formatCurrency(getEffective(tx, 'amount'))} | ${getEffective(tx, 'date')} | Page ${getEffective(tx, 'page_number') || '?'}`,
        data: effectiveData,
      });
    }
  };

  // Inline editing
  const startEdit = (e, txId, field) => {
    e.stopPropagation();
    if (editable) setEditingCell({ txId, field });
  };

  const commitEdit = (txId, field, value) => {
    setUndoStack(prev => [...prev, { corrections: { ...corrections }, addedRows: [...addedRows], deletedIds: new Set(deletedIds) }]);
    const nextCorr = {
      ...corrections,
      [txId]: { ...(corrections[txId] || {}), [field]: field === 'amount' ? parseFloat(value) || 0 : value }
    };
    setCorrections(nextCorr);
    setEditingCell(null);
    reportChange(nextCorr, addedRows, deletedIds);
  };

  const handleKeyDown = (e, txId, field) => {
    if (e.key === 'Enter') commitEdit(txId, field, e.target.value);
    if (e.key === 'Escape') setEditingCell(null);
  };

  // Insert row after a specific position
  const handleInsertRow = (e, afterTxId) => {
    e.stopPropagation();
    const newId = `new_${Date.now()}`;
    const newRow = { id: newId, date: '', description: '', amount: 0, page_number: '', _isNew: true };
    setUndoStack(prev => [...prev, { corrections: { ...corrections }, addedRows: [...addedRows], deletedIds: new Set(deletedIds) }]);

    // Find position in the effective list and insert after
    const baseTransactions = transactions.filter(t => !deletedIds.has(t.id));
    const allRows = [...baseTransactions, ...addedRows];
    const insertIdx = allRows.findIndex(t => t.id === afterTxId);
    if (insertIdx >= 0) {
      // Insert into addedRows at the right relative position
      const newAdded = [...addedRows];
      // Check if afterTxId is in addedRows
      const addedIdx = addedRows.findIndex(t => t.id === afterTxId);
      if (addedIdx >= 0) {
        newAdded.splice(addedIdx + 1, 0, newRow);
      } else {
        // Insert at beginning of addedRows but with an _insertAfter marker
        newRow._insertAfter = afterTxId;
        newAdded.push(newRow);
      }
      setAddedRows(newAdded);
      reportChange(corrections, newAdded, deletedIds);
    } else {
      const nextAdded = [...addedRows, newRow];
      setAddedRows(nextAdded);
      reportChange(corrections, nextAdded, deletedIds);
    }
  };

  // Add row at end
  const handleAddRow = () => {
    const newId = `new_${Date.now()}`;
    setUndoStack(prev => [...prev, { corrections: { ...corrections }, addedRows: [...addedRows], deletedIds: new Set(deletedIds) }]);
    const newRow = { id: newId, date: '', description: '', amount: 0, page_number: '', _isNew: true };
    const nextAdded = [...addedRows, newRow];
    setAddedRows(nextAdded);
    reportChange(corrections, nextAdded, deletedIds);
  };

  // Delete row — show confirmation dialog
  const handleDeleteRow = (e, txId) => {
    e.stopPropagation();
    setPendingDeleteId(txId);
  };

  const confirmDeleteRow = useCallback(() => {
    if (!pendingDeleteId) return;
    setUndoStack(prev => [...prev, { corrections: { ...corrections }, addedRows: [...addedRows], deletedIds: new Set(deletedIds) }]);
    const addedIdx = addedRows.findIndex(r => r.id === pendingDeleteId);
    if (addedIdx >= 0) {
      const nextAdded = addedRows.filter(r => r.id !== pendingDeleteId);
      setAddedRows(nextAdded);
      reportChange(corrections, nextAdded, deletedIds);
    } else {
      const nextDeleted = new Set([...deletedIds, pendingDeleteId]);
      setDeletedIds(nextDeleted);
      reportChange(corrections, addedRows, nextDeleted);
    }
    setPendingDeleteId(null);
  }, [pendingDeleteId, corrections, addedRows, deletedIds, reportChange]);

  // Undo
  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setCorrections(prev.corrections);
    setAddedRows(prev.addedRows);
    setDeletedIds(prev.deletedIds);
    setUndoStack(s => s.slice(0, -1));
    reportChange(prev.corrections, prev.addedRows, prev.deletedIds);
  };

  // Save all corrections
  const handleSave = () => {
    // Validate new rows have required fields
    const requiredFields = ['date', 'description', 'amount'];
    for (const row of addedRows) {
      const merged = { ...row, ...(corrections[row.id] || {}) };
      const missing = requiredFields.filter(f => !merged[f] && merged[f] !== 0);
      if (missing.length > 0) {
        alert(`New row is missing: ${missing.join(', ')}. Please fill in all fields before saving.`);
        return;
      }
    }
    if (onInteraction) {
      onInteraction({
        type: 'save_corrections',
        corrections,
        addedRows: addedRows.map(r => ({ ...r, ...(corrections[r.id] || {}) })),
        deletedIds: [...deletedIds],
      });
    }
  };

  const highlightSet = new Set(highlightIds);

  const renderCell = (tx, col, idx) => {
    const txId = tx.id;
    const isEditing = editingCell && editingCell.txId === txId && editingCell.field === col;
    const value = getEffective(tx, col);
    const isModified = corrections[txId] && corrections[txId][col] !== undefined;
    const isNew = tx._isNew;

    if (isEditing) {
      // Description gets a textarea for multi-line paste support
      if (col === 'description') {
        return (
          <textarea
            autoFocus
            className={styles.editInput}
            defaultValue={value || ''}
            onBlur={(e) => commitEdit(txId, col, e.target.value.replace(/\n/g, ' ').trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(txId, col, e.target.value.replace(/\n/g, ' ').trim()); }
              if (e.key === 'Escape') setEditingCell(null);
            }}
            rows={2}
            style={{ resize: 'vertical', minHeight: '24px' }}
          />
        );
      }
      return (
        <input
          autoFocus
          className={styles.editInput}
          defaultValue={value || ''}
          onBlur={(e) => commitEdit(txId, col, e.target.value.trim())}
          onKeyDown={(e) => handleKeyDown(e, txId, col)}
          type="text"
        />
      );
    }

    const cellClass = `${col === 'amount' ? styles.tdAmount : styles.td} ${isModified ? styles.modified : ''} ${isNew ? styles.newRow : ''}`;

    return (
      <td
        key={col}
        className={cellClass}
        onDoubleClick={(e) => startEdit(e, txId, col)}
        title={editable ? 'Double-click to edit' : undefined}
      >
        {col === 'amount' ? formatCurrency(value) :
         col === 'date' ? (value ? formatDate(value) : '') :
         value || ''}
      </td>
    );
  };

  return (
    <div className={styles.container}>
      {title && <h3 className={styles.title}>{title}</h3>}

      {/* Balance summary header */}
      {showRunningBalance && (
        <div className={styles.balanceSummary}>
          <span>Begin: <strong>{formatCurrency(beginBalance)}</strong></span>
          <span>End (scanned): <strong>{formatCurrency(endBalance)}</strong></span>
          <span>End (computed): <strong className={hasDiscrepancy ? styles.discrepancy : styles.balanceOk}>
            {formatCurrency(computedEndBalance)}
          </strong></span>
          {hasDiscrepancy && (
            <span className={styles.discrepancy}>
              Gap: {formatCurrency(computedEndBalance - endBalance)}
            </span>
          )}
        </div>
      )}

      {/* Toolbar */}
      {editable && (
        <div className={styles.toolbar}>
          <button className={styles.toolbarBtn} onClick={handleAddRow} title="Add transaction">+ Add Row</button>
          <button className={styles.toolbarBtn} onClick={handleUndo} disabled={undoStack.length === 0} title="Undo last change">↩ Undo</button>
          {hasCorrections && !onChange && (
            <button className={styles.toolbarBtnPrimary} onClick={handleSave}>Save Corrections</button>
          )}
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {editable && <th className={styles.thNarrow}></th>}
              <th className={styles.thNarrow}>#</th>
              {showPage && (
                <th className={styles.th} onClick={() => handleHeaderClick('page_number')}>
                  Pg{sortField === 'page_number' && <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                </th>
              )}
              {visibleColumns.map(col => (
                <th
                  key={col}
                  className={`${styles.th} ${col === 'amount' ? styles.thRight : ''}`}
                  onClick={() => handleHeaderClick(col)}
                >
                  {col === 'amount' ? 'Amount' : col.charAt(0).toUpperCase() + col.slice(1)}
                  {sortField === col && (
                    <span className={styles.sortIndicator}>
                      {sortDir === 'asc' ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </th>
              ))}
              {showRunningBalance && (
                <th className={`${styles.th} ${styles.thRight}`}>Balance</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((tx, idx) => {
              const isSelected = tx.id === selectedId;
              const isHighlighted = highlightSet.has(tx.id);
              const isNew = tx._isNew;
              const bal = runningBalances[idx];
              const balDrift = showRunningBalance && endBalance != null && idx === sorted.length - 1 && hasDiscrepancy;

              return (
                <tr
                  key={tx.id || idx}
                  className={`${styles.tr} ${isSelected ? styles.selected : ''} ${isHighlighted ? styles.highlighted : ''} ${isNew ? styles.newRow : ''}`}
                  onClick={() => handleRowClick(tx)}
                >
                  {editable && (
                    <td className={styles.tdAction}>
                      <button
                        className={styles.copyBtn}
                        onClick={(e) => handleCopyToChat(e, tx, idx + 1)}
                        title="Send to chat"
                      >💬</button>
                      <button
                        className={styles.insertBtn}
                        onClick={(e) => handleInsertRow(e, tx.id)}
                        title="Insert row after"
                      >+</button>
                      <button
                        className={styles.deleteBtn}
                        onClick={(e) => handleDeleteRow(e, tx.id)}
                        title="Remove"
                      >✕</button>
                    </td>
                  )}
                  <td className={styles.tdRowNum}>{idx + 1}</td>
                  {showPage && (
                    editingCell && editingCell.txId === tx.id && editingCell.field === 'page_number' ? (
                      <td className={styles.tdPage}>
                        <input
                          autoFocus
                          className={styles.editInput}
                          defaultValue={getEffective(tx, 'page_number') || ''}
                          onBlur={(e) => commitEdit(tx.id, 'page_number', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, tx.id, 'page_number')}
                          type="text"
                          style={{ width: '30px' }}
                        />
                      </td>
                    ) : (
                      <td className={styles.tdPage} onDoubleClick={(e) => startEdit(e, tx.id, 'page_number')} title={editable ? 'Double-click to edit' : undefined}>
                        {getEffective(tx, 'page_number') || ''}
                      </td>
                    )
                  )}
                  {visibleColumns.map(col => renderCell(tx, col, idx))}
                  {showRunningBalance && (
                    <td className={`${styles.tdBalance} ${balDrift ? styles.discrepancy : ''}`}>
                      {formatCurrency(bal)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {effectiveTransactions.length === 0 && (
          <div className={styles.empty}>No transactions to display</div>
        )}
      </div>
      <div className={styles.footer}>
        {effectiveTransactions.length} transaction{effectiveTransactions.length !== 1 ? 's' : ''}
        {showRunningBalance && ` · Sum: ${formatCurrency(computedEndBalance - (beginBalance || 0))}`}
        {hasCorrections && ' · Unsaved changes'}
      </div>
      <ConfirmBeforeActionDialog
        open={pendingDeleteId !== null}
        title="Remove Transaction"
        confirmMessage="Are you sure you want to remove this transaction?"
        proceedButtonLabel="Remove"
        cancelButtonLabel="Cancel"
        onProceed={confirmDeleteRow}
        onCancel={() => setPendingDeleteId(null)}
        list_element={() => null}
      />
    </div>
  );
}

export default TransactionTable;
